import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import LocalPizzaOutlinedIcon from "@mui/icons-material/LocalPizzaOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import {
  SERVICE_MODES,
  buildSelectionKey,
  fetchClientLocations,
  fetchLocationMenuTree,
  formatPrice,
  getCartCount,
  getDefaultSelections,
  getItemTotalQty,
  getMenuIframeConfig,
  getSelectedVariantLabel,
  postMenuNavigate,
  readCart,
  resolveDefaultLocation,
  resolveItemPrice,
  sortByDisplayOrder,
  syncIframeHeight,
  writeCart,
} from "./menuIframeHelpers";

const T1Menu = () => {
  const config = useMemo(() => getMenuIframeConfig(), []);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(
    config.selectedLocationId || ""
  );
  const [menuTree, setMenuTree] = useState(null);
  const [serviceMode, setServiceMode] = useState(
    config.defaultServiceMode || "delivery"
  );
  const [cart, setCart] = useState({});
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [error, setError] = useState("");

  const selectedLocation = useMemo(
    () => locations.find((row) => row.id === selectedLocationId) || null,
    [locations, selectedLocationId]
  );

  const sections = useMemo(
    () => sortByDisplayOrder(menuTree?.menu || []),
    [menuTree]
  );

  useEffect(() => {
    let mounted = true;

    const loadLocations = async () => {
      if (!config.client_id) {
        setError("Missing client_id in iframe params.");
        setLoadingLocations(false);
        return;
      }

      try {
        setLoadingLocations(true);
        setError("");

        const data = await fetchClientLocations({
          apiBaseUrl: config.apiBaseUrl,
          client_id: config.client_id,
        });

        if (!mounted) return;

        setLocations(data);

        const defaultLocation = resolveDefaultLocation(
          data,
          config.selectedLocationId
        );

        setSelectedLocationId(defaultLocation?.id || "");
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Unable to fetch restaurant branches.");
      } finally {
        if (mounted) {
          setLoadingLocations(false);
        }
      }
    };

    loadLocations();

    return () => {
      mounted = false;
    };
  }, [config.apiBaseUrl, config.client_id, config.selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId || !config.client_id) return;

    let mounted = true;

    const loadMenu = async () => {
      try {
        setLoadingMenu(true);
        setError("");

        const data = await fetchLocationMenuTree({
          apiBaseUrl: config.apiBaseUrl,
          client_id: config.client_id,
          locationId: selectedLocationId,
        });

        if (!mounted) return;
        setMenuTree(data);
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Unable to fetch menu.");
      } finally {
        if (mounted) {
          setLoadingMenu(false);
        }
      }
    };

    loadMenu();

    return () => {
      mounted = false;
    };
  }, [config.apiBaseUrl, config.client_id, selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId || !config.client_id) return;

    setCart(
      readCart({
        client_id: config.client_id,
        locationId: selectedLocationId,
      })
    );
  }, [config.client_id, selectedLocationId]);

  useEffect(() => {
    const resize = () => syncIframeHeight("t1-menu");
    const timer = window.setTimeout(resize, 120);

    window.addEventListener("resize", resize);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", resize);
    };
  }, [
    locations,
    menuTree,
    cart,
    loadingLocations,
    loadingMenu,
    serviceMode,
    selectedLocationId,
  ]);

  const persistCart = (nextCart) => {
    setCart(nextCart);
    writeCart({
      client_id: config.client_id,
      locationId: selectedLocationId,
      cart: nextCart,
    });
    syncIframeHeight("t1-menu");
  };

  const updateCartItem = (item, selections = {}, delta = 1) => {
    const nextCart = { ...cart };
    const selectionKey = buildSelectionKey(item, selections);
    const currentQuantity = Number(nextCart[selectionKey]?.quantity || 0);
    const nextQuantity = currentQuantity + delta;
    const priceMeta = resolveItemPrice(item, serviceMode, selections);

    if (nextQuantity <= 0) {
      delete nextCart[selectionKey];
    } else {
      nextCart[selectionKey] = {
        key: selectionKey,
        itemId: item.id,
        itemName: item.name,
        image: item.image || "",
        quantity: nextQuantity,
        unitPrice: Number(priceMeta.finalPrice ?? priceMeta.basePrice ?? 0),
        locationId: selectedLocationId,
        serviceMode,
        selectedVariantLabel: getSelectedVariantLabel(selections),
      };
    }

    persistCart(nextCart);
  };

  const getLocationLabel = (location) => {
    const extra = [location?.city, location?.address_line1]
      .filter(Boolean)
      .join(" • ");
    return extra ? `${location?.name} — ${extra}` : location?.name || "Branch";
  };

  const QuantityControl = ({ quantity, onMinus, onPlus }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        border: `1px solid ${config.primaryColor}33`,
        borderRadius: "999px",
        px: 0.5,
        py: 0.25,
      }}
    >
      <IconButton size="small" onClick={onMinus}>
        <RemoveOutlinedIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 24, textAlign: "center", fontWeight: 700 }}>
        {quantity}
      </Typography>
      <IconButton size="small" onClick={onPlus}>
        <AddOutlinedIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const renderItemCard = (item) => {
    const defaultSelections = getDefaultSelections(item);
    const priceMeta = resolveItemPrice(item, serviceMode, defaultSelections);
    const hasVariants = Boolean(
      item?.variant_groups?.length || item?.direct_variants?.length
    );
    const totalQty = getItemTotalQty(cart, item.id);
    const baseKey = buildSelectionKey(item, defaultSelections);
    const baseQty = Number(cart[baseKey]?.quantity || 0);
    const priceText = hasVariants
      ? `From ${formatPrice(
          priceMeta.finalPrice ?? priceMeta.basePrice,
          config.currency
        )}`
      : formatPrice(priceMeta.finalPrice ?? priceMeta.basePrice, config.currency);

    return (
      <Card
        key={item.id}
        sx={{
          borderRadius: 4,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: `1px solid ${config.secondaryColor}`,
          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          onClick={() =>
            postMenuNavigate(`/menu/${item.id}?locationId=${selectedLocationId}`)
          }
          sx={{ cursor: "pointer" }}
        >
          {item.image ? (
            <Box
              component="img"
              src={item.image}
              alt={item.name}
              onLoad={() => syncIframeHeight("t1-menu")}
              sx={{
                width: "100%",
                height: 190,
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                height: 190,
                background: `linear-gradient(135deg, ${config.secondaryColor} 0%, ${config.bgColor} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalPizzaOutlinedIcon
                sx={{ fontSize: 56, color: config.primaryColor }}
              />
            </Box>
          )}
        </Box>

        <CardContent sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: item?.is_veg ? "#16a34a" : "#dc2626",
                    flexShrink: 0,
                  }}
                />
                {item?.badge ? (
                  <Chip
                    label={item.badge}
                    size="small"
                    sx={{
                      height: 22,
                      bgcolor: `${config.primaryColor}18`,
                      color: config.primaryColor,
                      fontWeight: 700,
                    }}
                  />
                ) : null}
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: config.textColor,
                  lineHeight: 1.2,
                }}
              >
                {item.name}
              </Typography>
            </Box>

            {totalQty > 0 ? (
              <Chip
                label={`${totalQty} in cart`}
                size="small"
                sx={{
                  bgcolor: `${config.primaryColor}14`,
                  color: config.primaryColor,
                  fontWeight: 700,
                }}
              />
            ) : null}
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1,
              lineHeight: 1.6,
              minHeight: 48,
            }}
          >
            {item.description || "Freshly prepared and served hot."}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1.5 }}
          >
            <Chip
              icon={<StarRoundedIcon />}
              label={`${item.rating || "4.0"} (${item.rating_count || 0})`}
              size="small"
              variant="outlined"
            />
            {item.prep_time_minutes ? (
              <Chip
                icon={<AccessTimeOutlinedIcon />}
                label={`${item.prep_time_minutes} min`}
                size="small"
                variant="outlined"
              />
            ) : null}
          </Stack>

          {item?.variant_groups?.[0]?.variants?.length ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1.2,
                color: "text.secondary",
              }}
            >
              {sortByDisplayOrder(item.variant_groups[0].variants)
                .map((variant) => variant.name)
                .join(" • ")}
            </Typography>
          ) : null}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            spacing={2}
            sx={{ mt: "auto", pt: 2 }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  color: config.primaryColor,
                  lineHeight: 1,
                }}
              >
                {priceText}
              </Typography>

              {priceMeta.originalPrice ? (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    textDecoration: "line-through",
                  }}
                >
                  {formatPrice(priceMeta.originalPrice, config.currency)}
                </Typography>
              ) : null}
            </Box>

            {hasVariants ? (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => updateCartItem(item, defaultSelections, 1)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    backgroundColor: config.primaryColor,
                    px: 2.2,
                    fontWeight: 700,
                    "&:hover": {
                      backgroundColor: config.primaryColor,
                    },
                  }}
                >
                  Add
                </Button>

                <Button
                  variant="outlined"
                  onClick={() =>
                    postMenuNavigate(
                      `/menu/${item.id}?locationId=${selectedLocationId}`
                    )
                  }
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    borderColor: `${config.primaryColor}44`,
                    color: config.primaryColor,
                    fontWeight: 700,
                  }}
                >
                  Customize
                </Button>
              </Stack>
            ) : baseQty > 0 ? (
              <QuantityControl
                quantity={baseQty}
                onMinus={() => updateCartItem(item, defaultSelections, -1)}
                onPlus={() => updateCartItem(item, defaultSelections, 1)}
              />
            ) : (
              <Button
                variant="contained"
                onClick={() => updateCartItem(item, defaultSelections, 1)}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  backgroundColor: config.primaryColor,
                  px: 2.2,
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: config.primaryColor,
                  },
                }}
              >
                Add to cart
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${config.bgColor} 0%, ${config.secondaryColor} 100%)`,
        py: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth="xl">
        <Card
          sx={{
            borderRadius: 5,
            mb: 3,
            backgroundColor: config.surfaceColor,
            border: `1px solid ${config.secondaryColor}`,
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", lg: "center" }}
              spacing={2}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: config.textColor,
                    fontSize: { xs: "1.7rem", md: "2.2rem" },
                  }}
                >
                  {config.restaurantName} Menu
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ mt: 0.75, color: "text.secondary" }}
                >
                  {menuTree?.location_name ||
                    selectedLocation?.name ||
                    "Choose a branch"}{" "}
                  • Fresh items, variants and quick add-to-cart
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <FormControl fullWidth sx={{ minWidth: { md: 320 } }}>
                  <InputLabel>Branch</InputLabel>
                  <Select
                    label="Branch"
                    value={selectedLocationId}
                    onChange={(e) => setSelectedLocationId(e.target.value)}
                    startAdornment={
                      <LocationOnOutlinedIcon sx={{ mr: 1, color: "action.active" }} />
                    }
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {getLocationLabel(location)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  exclusive
                  value={serviceMode}
                  onChange={(event, nextValue) => {
                    if (nextValue) setServiceMode(nextValue);
                  }}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      borderRadius: "999px !important",
                      px: 2,
                      borderColor: `${config.primaryColor}22`,
                      color: config.textColor,
                    },
                    "& .Mui-selected": {
                      backgroundColor: `${config.primaryColor}14 !important`,
                      color: `${config.primaryColor} !important`,
                      fontWeight: 800,
                    },
                  }}
                >
                  {SERVICE_MODES.map((mode) => (
                    <ToggleButton key={mode.value} value={mode.value}>
                      {mode.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <Chip
                  icon={<ShoppingCartCheckoutOutlinedIcon />}
                  label={`${getCartCount(cart)} item(s)`}
                  sx={{
                    height: 42,
                    px: 1,
                    bgcolor: `${config.primaryColor}12`,
                    color: config.primaryColor,
                    fontWeight: 800,
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {sections.length > 0 ? (
          <Card
            sx={{
              borderRadius: 4,
              mb: 3,
              backgroundColor: config.surfaceColor,
              border: `1px solid ${config.secondaryColor}`,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {sections.map((section) => (
                  <Chip
                    key={section.id}
                    clickable
                    label={section.name}
                    onClick={() =>
                      document
                        .getElementById(`section-${section.id}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                    sx={{
                      bgcolor: `${config.primaryColor}10`,
                      color: config.primaryColor,
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        ) : null}

        {loadingLocations || loadingMenu ? (
          <Box
            sx={{
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <CircularProgress sx={{ color: config.primaryColor }} />
              <Typography color="text.secondary">Loading menu...</Typography>
            </Stack>
          </Box>
        ) : null}

        {!loadingLocations &&
          !loadingMenu &&
          !error &&
          sections.map((section) => (
            <Box
              key={section.id}
              id={`section-${section.id}`}
              sx={{ mb: 4, scrollMarginTop: 90 }}
            >
              <Card
                sx={{
                  borderRadius: 5,
                  backgroundColor: config.surfaceColor,
                  border: `1px solid ${config.secondaryColor}`,
                  boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 900, color: config.textColor }}
                      >
                        {section.name}
                      </Typography>
                      {section.description ? (
                        <Typography sx={{ mt: 0.75, color: "text.secondary" }}>
                          {section.description}
                        </Typography>
                      ) : null}
                    </Box>

                    <Chip
                      label={section.is_veg ? "Veg section" : "Non-veg section"}
                      sx={{
                        bgcolor: section.is_veg ? "#E8FCEB" : "#FDECEC",
                        color: section.is_veg ? "#15803d" : "#b91c1c",
                        fontWeight: 800,
                      }}
                    />
                  </Stack>

                  <Divider sx={{ my: 2.5 }} />

                  {sortByDisplayOrder(section.categories).map((category) => (
                    <Box key={category.id} sx={{ mb: 3.5 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ mb: 2 }}
                      >
                        <Chip
                          label={category.name}
                          sx={{
                            bgcolor: `${config.primaryColor}12`,
                            color: config.primaryColor,
                            fontWeight: 800,
                          }}
                        />

                        {category.description ? (
                          <Typography color="text.secondary">
                            {category.description}
                          </Typography>
                        ) : null}
                      </Stack>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)",
                            xl: "repeat(3, 1fr)",
                          },
                          gap: 2,
                        }}
                      >
                        {sortByDisplayOrder(category.items).map(renderItemCard)}
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          ))}
      </Container>
    </Box>
  );
};

export default T1Menu;